"use client";

/**
 * CtaStepSequenceDialog
 * The one dialog a multi-step CTA runs in. It stays open across steps and swaps its content: a
 * loading or error state, the returning guest's steps overview, or one step (a FormModal or
 * HotelBookingModal rendered `embedded`). A step opened from the overview gets "‹ All steps" above
 * its title and previous/next links; leaving a step with unsaved changes asks first, inline. The
 * dialog is a small card, widening only while a step's form is open for input.
 *
 * State lives in `useCtaStepSequence`; fetching and starting live in `useLinkedStepsCta`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FetchModal from "@/fetch-ui/FetchModal";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchCallout } from "@/fetch-ui/FetchCallout";
import { useToast } from "@/components/providers/FetchToastProvider";
import {
  cancelOwnHotelBookingAction,
  restoreOwnHotelBookingAction,
} from "@/features/hotel-booking/_logic/hotel-booking-guest-actions";
import type { CtaHotelStepOverview, CtaStep, CtaStepSequenceContext, CtaStepsOverview } from "../../_logic/cta-steps";
import { describeOverviewStep, hasDeclinedStep } from "../../_logic/cta-steps-overview-display";
import { invalidateHotelBookingEmbedBundleCache } from "../../_logic/hotel-booking-embed-bundle-cache";
import type { useCtaStepSequence } from "../../_hooks/useCtaStepSequence";
import { useUnsavedStepGuard } from "../../_hooks/useUnsavedStepGuard";
import { CtaStepsOverviewPanel, type OverviewRoomActions } from "./CtaStepsOverviewPanel";
import { FormModal } from "./FormModal";
import { LazyHotelBookingModal } from "./LazyHotelBookingModal";
import { PUBLIC_MODAL_COMPACT_CLASSNAME, PUBLIC_MODAL_FULL_SCREEN_MOBILE_CLASSNAME } from "./public-modal-classnames";

/** The sequence runner's state and actions. */
type SequenceRunner = ReturnType<typeof useCtaStepSequence>;

/** The overview data and its reload, owned by `useLinkedStepsCta`. */
export interface CtaOverviewSource {
  data: CtaStepsOverview | null;
  /** Re-fetches the overview (after a change). */
  refresh: () => Promise<void>;
  /** Retries the initial load after an error. */
  retry: () => void;
}

interface CtaStepSequenceDialogProps {
  steps: CtaStep[];
  runner: SequenceRunner;
  overview: CtaOverviewSource;
  display: { title: string; eventId: number; primaryColor: string };
}

/** "All steps" link above the title; py-3 keeps the tap target at 44px, -my-3 keeps it compact. */
const BACK_LINK_CLASS =
  "-my-3 inline-flex self-start touch-manipulation items-center gap-1 rounded py-3 pr-2 text-sm font-medium text-gray-600 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

/** Fallback step name before the overview has loaded (or for an anonymous guest). */
function fallbackName(step: CtaStep | undefined): string {
  return step?.kind === "hotel-booking" ? "Hotel booking" : "Form";
}

/**
 * Renders the multi-step CTA dialog, or nothing when the sequence isn't running.
 * @param props - See {@link CtaStepSequenceDialogProps}
 * @returns The dialog, or null
 * @throws Never
 */
export function CtaStepSequenceDialog({
  steps,
  runner,
  overview,
  display,
}: CtaStepSequenceDialogProps): JSX.Element | null {
  // Hooks
  const { showToast } = useToast();
  const { state, complete: completeStep, close, openOverview, goTo, startRun } = runner;
  const viewKey = state ? (state.view === "step" ? `step-${state.index}` : state.view) : "closed";
  const guard = useUnsavedStepGuard(viewKey);

  // States
  const [pendingBookingId, setPendingBookingId] = useState<number | null>(null);
  // Bookings the guest chose to keep after declining. Lives here, not in the overview panel, so the
  // choice holds across steps and reopening the dialog while the page is open.
  const [keptBookingIds, setKeptBookingIds] = useState<ReadonlySet<number>>(() => new Set());
  // The view before the current one, to spot an arrival on the overview from a step.
  const previousViewRef = useRef<string | null>(null);

  // Derived values
  const stepIndex = state?.view === "step" ? state.index : null;
  const step = stepIndex === null ? undefined : steps[stepIndex];
  const stepOverview = stepIndex === null ? undefined : overview.data?.steps[stepIndex];
  const formOverview = stepOverview?.kind === "form" ? stepOverview : undefined;
  const nameOf = useCallback(
    (index: number) => overview.data?.steps[index]?.name ?? fallbackName(steps[index]),
    [overview.data, steps]
  );
  const openableIndexes = useMemo(() => {
    if (!overview.data) return [];
    const hasDeclined = hasDeclinedStep(overview.data);
    return overview.data.steps.flatMap((s, i) => (describeOverviewStep(s, hasDeclined).openable ? [i] : []));
  }, [overview.data]);

  const sequence = useMemo<CtaStepSequenceContext | null>(() => {
    if (state?.view !== "step") return null;
    const { index, contact, origin } = state;
    const fromOverview = origin === "overview";
    return {
      origin,
      stepNumber: index + 1,
      stepCount: steps.length,
      // From the overview, completing a step goes back to the overview, never on to the next step.
      hasNextStep: !fromOverview && index + 1 < steps.length,
      prefillContact: contact,
      onStepComplete: (completion) => completeStep(index, completion),
      recap: fromOverview ? (formOverview?.recap ?? null) : null,
      editTarget: fromOverview ? (formOverview?.editTarget ?? null) : null,
      allowsResubmit: formOverview?.allowsResubmit ?? false,
      onEditingChange: guard.setIsEditing,
    };
  }, [state, steps.length, completeStep, formOverview, guard.setIsEditing]);

  // Handlers
  const { guardLeave } = guard;
  const handleClose = useCallback(() => guardLeave(close), [guardLeave, close]);
  const handleBackToOverview = useCallback(() => guardLeave(() => openOverview()), [guardLeave, openOverview]);
  const handleGoTo = useCallback((index: number) => guardLeave(() => goTo(index)), [guardLeave, goTo]);
  const handleStartRun = useCallback(() => startRun(), [startRun]);

  /** After a guest changes a booking, drop the cached embed bundle so the step reloads fresh. */
  const afterBookingChange = useCallback(
    async (target: CtaHotelStepOverview) => {
      invalidateHotelBookingEmbedBundleCache(display.eventId, target.bookingLinkId);
      await overview.refresh();
    },
    [display.eventId, overview]
  );

  const handleRestoreRoom = useCallback(
    async (target: CtaHotelStepOverview) => {
      if (target.responseId == null) return;
      setPendingBookingId(target.responseId);
      const result = await restoreOwnHotelBookingAction(target.responseId);
      setPendingBookingId(null);
      if (!result.success) {
        showToast(result.error, "error");
        return;
      }
      showToast("Your room is restored", "success");
      await afterBookingChange(target);
    },
    [afterBookingChange, showToast]
  );

  const handleReleaseRoom = useCallback(
    async (target: CtaHotelStepOverview) => {
      if (target.responseId == null) return;
      setPendingBookingId(target.responseId);
      const result = await cancelOwnHotelBookingAction(target.responseId);
      setPendingBookingId(null);
      if (!result.success) {
        showToast(result.error, "error");
        return;
      }
      showToast("Room released", "success", {
        action: { label: "Undo", onClick: () => void handleRestoreRoom(target) },
      });
      await afterBookingChange(target);
    },
    [afterBookingChange, handleRestoreRoom, showToast]
  );

  const handleKeepRoom = useCallback((target: CtaHotelStepOverview) => {
    const responseId = target.responseId;
    if (responseId != null) setKeptBookingIds((kept) => new Set(kept).add(responseId));
  }, []);

  const roomActions = useMemo<OverviewRoomActions>(
    () => ({
      pendingBookingId,
      keptBookingIds,
      onKeep: handleKeepRoom,
      onRelease: (target) => void handleReleaseRoom(target),
      onRestore: (target) => void handleRestoreRoom(target),
    }),
    [pendingBookingId, keptBookingIds, handleKeepRoom, handleReleaseRoom, handleRestoreRoom]
  );

  // Effects
  // Back on the overview from a step: confirm the save and refresh the statuses. Keyed on the
  // arrival (the previous view was a step), so the first load and later re-renders don't refetch.
  useEffect(() => {
    const previousView = previousViewRef.current;
    previousViewRef.current = state?.view ?? null;
    if (state?.view !== "overview" || previousView !== "step") return;
    if (state.updatedIndex !== null) showToast(`${nameOf(state.updatedIndex)} saved`, "success");
    void overview.refresh();
  }, [state, nameOf, overview, showToast]);

  // Render
  if (!state) return null;

  const fromOverview = state.view === "step" && state.origin === "overview";
  const position = stepIndex === null ? -1 : openableIndexes.indexOf(stepIndex);
  const previousIndex = fromOverview && position > 0 ? openableIndexes[position - 1] : null;
  const nextIndex =
    fromOverview && position !== -1 && position < openableIndexes.length - 1 ? openableIndexes[position + 1] : null;

  const title = stepIndex === null ? display.title : nameOf(stepIndex);
  const subtitle = fromOverview && stepIndex !== null ? `Step ${stepIndex + 1} of ${steps.length}` : undefined;

  // Wide only while a step's form is open for input; recaps, summaries, and the overview stay small.
  const isWide = state.view === "step" && guard.isEditing;

  const bottomBar =
    previousIndex !== null || nextIndex !== null ? (
      <nav aria-label="Steps" className="flex items-center justify-between gap-2">
        {previousIndex !== null ? (
          <FetchButton
            variant="ghost"
            leftIcon={<ChevronLeft className="h-4 w-4" />}
            onClick={() => handleGoTo(previousIndex)}
          >
            <span className="block max-w-[10rem] truncate sm:max-w-[14rem]">{nameOf(previousIndex)}</span>
          </FetchButton>
        ) : (
          <span />
        )}
        {nextIndex !== null && (
          <FetchButton
            variant="ghost"
            rightIcon={<ChevronRight className="h-4 w-4" />}
            onClick={() => handleGoTo(nextIndex)}
          >
            <span className="block max-w-[10rem] truncate sm:max-w-[14rem]">{nameOf(nextIndex)}</span>
          </FetchButton>
        )}
      </nav>
    ) : undefined;

  return (
    <FetchModal
      isOpen
      onClose={handleClose}
      ariaLabel={subtitle ? `${title}, ${subtitle.toLowerCase()}` : title}
      floatingClose
      bottomBar={bottomBar}
      size="auto"
      className={isWide ? PUBLIC_MODAL_FULL_SCREEN_MOBILE_CLASSNAME : PUBLIC_MODAL_COMPACT_CLASSNAME}
      dismissOnBackdrop={!guard.isDirty}
    >
      {/* Our own header (FetchModal's puts extra links beside the title, not above it). Stays put
          while the body below scrolls. */}
      <div className="flex flex-shrink-0 flex-col gap-1 px-1 pb-4 pr-10">
        {fromOverview && (
          <button type="button" onClick={handleBackToOverview} className={BACK_LINK_CLASS}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            All steps
          </button>
        )}
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm font-medium text-gray-500">{subtitle}</p>}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-1">
        {guard.isAsking && (
          <div className="mb-4">
            <FetchCallout variant="warning">
              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium">Discard your changes?</p>
                <div className="flex flex-wrap gap-2">
                  <FetchButton variant="primary" onClick={guard.keepEditing}>
                    Keep editing
                  </FetchButton>
                  <FetchButton variant="outline" onClick={guard.discard}>
                    Discard
                  </FetchButton>
                </div>
              </div>
            </FetchCallout>
          </div>
        )}

        {state.view === "loading" && (
          <div aria-busy="true" aria-label="Loading your steps" className="flex flex-col gap-2">
            {steps.map((_, index) => (
              <div key={index} className="h-14 animate-pulse rounded-lg bg-gray-100" />
            ))}
          </div>
        )}

        {state.view === "error" && (
          <div role="alert" className="flex flex-col items-start gap-3 py-4">
            <p className="text-sm font-medium text-gray-700">We couldn&apos;t load your steps.</p>
            <div className="flex flex-wrap gap-2">
              <FetchButton variant="primary" onClick={overview.retry}>
                Retry
              </FetchButton>
              <FetchButton variant="outline" onClick={handleStartRun}>
                Start from step 1
              </FetchButton>
            </div>
          </div>
        )}

        {state.view === "overview" && overview.data && (
          <CtaStepsOverviewPanel
            overview={overview.data}
            allSet={state.allSet}
            onOpenStep={goTo}
            onDone={close}
            roomActions={roomActions}
          />
        )}

        {step && sequence && stepIndex !== null && (
          // Input anywhere in the step marks it dirty while its form is open (see onEditingChange).
          <div onInputCapture={guard.markInput} onChangeCapture={guard.markInput}>
            {step.kind === "form" ? (
              <FormModal
                key={`step-${stepIndex}`}
                isOpen
                embedded
                onClose={close}
                formId={step.formId}
                eventId={display.eventId}
                primaryColor={display.primaryColor}
                inviteToken={step.inviteToken}
                sequence={sequence}
              />
            ) : (
              <LazyHotelBookingModal
                key={`step-${stepIndex}`}
                isOpen
                embedded
                onClose={close}
                eventId={display.eventId}
                bookingLinkId={step.bookingLinkId}
                sequence={sequence}
              />
            )}
          </div>
        )}
      </div>
    </FetchModal>
  );
}
